import { useCallback, useEffect, useState } from "react";

import { isReferencedNodeValue } from "@doitintl/cloudflow-commons";
import {
  type NodeTransformationConcatenation,
  type NodeTransformationLowerCase,
  NodeTransformationType,
  type NodeTransformationUpperCase,
  type ReferencedNodeValue,
} from "@doitintl/cmp-models";
import { Box, Typography } from "@mui/material";
import { useFormikContext } from "formik";

import { cloudflowTexts } from "../../../../../../assets/texts";
import { ReadonlyReferencedFieldChip } from "../../../ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldChip";
import { useFieldCommonProps } from "../../../ApiActionParametersForm/useFieldCommonProps";
import { generateConcatenationSchema, generateStringTransformationCaseSchema } from "./schemaValidators";
import { useTransformationNodeSchemaContext } from "./TransformationNodeForm";

export const useConcatenationForm = () => {
  const { setTransformationSchema, referenceableNodes } = useTransformationNodeSchemaContext();
  const { getFieldProps, setFieldValue } = useFormikContext();
  const { value: rootReferencedNodeValue }: { value: ReferencedNodeValue | undefined } =
    getFieldProps("referencedNodeField");
  const [validationSchema, setValidationSchema] = useState(
    generateConcatenationSchema(referenceableNodes, rootReferencedNodeValue)
  );

  const { value: transformationFieldValue }: { value: NodeTransformationConcatenation | undefined } =
    getFieldProps("transformation");
  const newFieldNameCommonProps = useFieldCommonProps(
    getFieldProps("transformation.newFieldName"),
    cloudflowTexts.NEW_FIELD_NAME,
    true
  );

  useEffect(() => {
    setTransformationSchema(validationSchema);
  }, [setTransformationSchema, validationSchema]);

  useEffect(() => {
    setValidationSchema(generateConcatenationSchema(referenceableNodes, rootReferencedNodeValue));
  }, [referenceableNodes, rootReferencedNodeValue]);

  useEffect(() => {
    if (!transformationFieldValue || transformationFieldValue.type !== NodeTransformationType.CONCATENATION) {
      setFieldValue("transformation", validationSchema.getDefault());
    }
  }, [setFieldValue, validationSchema, transformationFieldValue]);

  const addField: () => void = useCallback(() => {
    const payload = transformationFieldValue?.payload ?? [];
    setFieldValue("transformation.payload", [...payload, rootReferencedNodeValue]);
  }, [rootReferencedNodeValue, setFieldValue, transformationFieldValue?.payload]);

  const removeField: (index: number) => void = useCallback(
    (index: number) => {
      const payload = transformationFieldValue?.payload ?? [];
      setFieldValue(
        "transformation.payload",
        payload.filter((_, i: number) => i !== index)
      );
    },
    [setFieldValue, transformationFieldValue?.payload]
  );

  const renderTags = useCallback(
    (value: (string | ReferencedNodeValue)[]) => {
      const mergedValues = value.reduce<(string | ReferencedNodeValue)[]>((acc, curr) => {
        const prev = acc[acc.length - 1];
        if (typeof curr === "string" && typeof prev === "string") {
          acc[acc.length - 1] = `${prev}${curr}`;
        } else {
          acc.push(curr);
        }
        return acc;
      }, []);

      return mergedValues.map((v, index) => (
        <Box
          key={index}
          sx={{
            mr: 1,
          }}
        >
          {isReferencedNodeValue(v) ? (
            <ReadonlyReferencedFieldChip
              referenceableNodes={referenceableNodes}
              value={v}
              onDelete={() => {
                removeField(index);
              }}
            />
          ) : (
            <Typography sx={{ color: (theme) => theme.palette.text.primary }}>{v}</Typography>
          )}
        </Box>
      ));
    },
    [removeField, referenceableNodes]
  );
  return {
    addField,
    newFieldNameCommonProps,
    transformationFieldValue,
    referenceableNodes,
    removeField,
    getFieldProps,
    renderTags,
  };
};

export const useStringTransformationForm = (
  type: NodeTransformationType.UPPER_CASE | NodeTransformationType.LOWER_CASE
) => {
  const { setTransformationSchema, referenceableNodes } = useTransformationNodeSchemaContext();
  const { getFieldProps, setFieldValue } = useFormikContext();
  const { value: rootReferencedNodeValue }: { value: ReferencedNodeValue | undefined } =
    getFieldProps("referencedNodeField");

  const { value: transformationFieldValue } = getFieldProps<
    NodeTransformationUpperCase | NodeTransformationLowerCase | undefined
  >("transformation");

  const newFieldNameCommonProps = useFieldCommonProps(
    getFieldProps("transformation.newFieldName"),
    cloudflowTexts.NEW_FIELD_NAME,
    true
  );
  const payloadCommonProps = useFieldCommonProps(getFieldProps("transformation.payload"), "Referenced value", true);

  useEffect(() => {
    const schema = generateStringTransformationCaseSchema(type, referenceableNodes, rootReferencedNodeValue);
    setTransformationSchema(schema);

    if (!transformationFieldValue || transformationFieldValue.type !== type) {
      setFieldValue("transformation", schema.getDefault());
    }
    // NOTE: formik does not support callback version of setFieldValue, it has been requested but the project is
    // abandoned, ignoring 'transformationFieldValue' here instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceableNodes, rootReferencedNodeValue, setFieldValue, setTransformationSchema, type]);

  return {
    newFieldNameCommonProps,
    transformationFieldValue,
    referenceableNodes,
    getFieldProps,
    payloadCommonProps,
  };
};
